import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { IconBillCheck } from '../../components/layout/icons';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useToast } from '../../lib/toast';
import { toDateTimeInput } from '../ipd-daycare/ipd-daycare.utils';
import { useDialysisPrescription, useSaveDialysisPrescription } from './dialysis.queries';
import { dateTimeInput } from './dialysis.utils';
import type { DialysisAdmission, UpsertDialysisPrescriptionPayload } from './types';

interface VitalsRow { height: string; weight: string; pulse: string; bpSystolic: string; bpDiastolic: string; temperature: string }
interface ComplaintRow { date: string; complaint: string; frequency: string; severity: string; duration: string }
interface DiagnosisRow { date: string; diagnosis: string; duration: string }
interface MedicineRow { composition: string; medicine: string; dose: string; when: string; frequency: string; duration: string; notes: string }

const blankVitals = (): VitalsRow => ({ height: '0', weight: '0', pulse: '0', bpSystolic: '0', bpDiastolic: '0', temperature: '0' });
const blankComplaint = (): ComplaintRow => ({ date: '', complaint: '', frequency: '', severity: '', duration: '' });
const blankDiagnosis = (): DiagnosisRow => ({ date: '', diagnosis: '', duration: '' });
const blankMedicine = (): MedicineRow => ({ composition: '', medicine: '', dose: '', when: '', frequency: '', duration: '', notes: '' });

const FREQUENCY_OPTIONS = ['Once a day', 'Twice a day', 'Thrice a day', 'Four times a day'];
const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe'];
const DURATION_OPTIONS = ['1 Day', '3 Days', '5 Days', '7 Days', '10 Days', '15 Days', '1 Month'];
const DIAGNOSIS_OPTIONS = ['Hypertension', 'Type 2 Diabetes Mellitus', 'Common Cold', 'Viral Fever', 'Migraine', 'Gastritis', 'Upper Respiratory Tract Infection'];
const COMPOSITION_OPTIONS = ['Paracetamol', 'Amoxicillin', 'Metformin', 'Amlodipine', 'Omeprazole', 'Cetirizine'];
const MEDICINE_OPTIONS = ['Paracetamol 500mg', 'Amoxicillin 500mg', 'Metformin 500mg', 'Amlodipine 5mg', 'Omeprazole 20mg', 'Cetirizine 10mg'];
const DOSE_OPTIONS = ['1 Tablet', '2 Tablets', '1/2 Tablet', '5 ml', '10 ml'];
const WHEN_OPTIONS = ['Before Food', 'After Food', 'Empty Stomach', 'With Food'];

const Select = ({ value, options, onChange }: { value: string; options: string[]; onChange: (value: string) => void }) => (
  <select value={value} onChange={(event) => onChange(event.target.value)}>
    <option value="">Select</option>
    {options.map((option) => <option key={option} value={option}>{option}</option>)}
  </select>
);

const text = (value: string | null) => value ?? '';
const dateText = (value: string | null) => value ? value.slice(0, 10) : '';

export const DialysisPrescriptionForm = ({ admission }: { admission: DialysisAdmission }) => {
  const { showToast } = useToast();
  const query = useDialysisPrescription(admission.id);
  const saveMutation = useSaveDialysisPrescription(admission.id);
  const [prescriptionDate, setPrescriptionDate] = useState(dateTimeInput);
  const [vitals, setVitals] = useState<VitalsRow[]>([blankVitals()]);
  const [complaints, setComplaints] = useState<ComplaintRow[]>([blankComplaint()]);
  const [diagnoses, setDiagnoses] = useState<DiagnosisRow[]>([blankDiagnosis()]);
  const [medicines, setMedicines] = useState<MedicineRow[]>([blankMedicine()]);
  const [testsNeeded, setTestsNeeded] = useState('');
  const [advice, setAdvice] = useState('');

  useEffect(() => {
    if (!query.data) return;
    setPrescriptionDate(toDateTimeInput(query.data.prescriptionDate));
    setVitals(query.data.vitals.length > 0
      ? query.data.vitals.map((row) => ({ ...row, height: text(row.height), weight: text(row.weight), pulse: text(row.pulse), bpSystolic: text(row.bpSystolic), bpDiastolic: text(row.bpDiastolic), temperature: text(row.temperature) }))
      : [blankVitals()]);
    setComplaints(query.data.complaints.length > 0
      ? query.data.complaints.map((row) => ({ date: dateText(row.date), complaint: text(row.complaint), frequency: text(row.frequency), severity: text(row.severity), duration: text(row.duration) }))
      : [blankComplaint()]);
    setDiagnoses(query.data.diagnoses.length > 0
      ? query.data.diagnoses.map((row) => ({ date: dateText(row.date), diagnosis: text(row.diagnosis), duration: text(row.duration) }))
      : [blankDiagnosis()]);
    setMedicines(query.data.medicines.length > 0
      ? query.data.medicines.map((row) => ({ composition: text(row.composition), medicine: text(row.medicine), dose: text(row.dose), when: text(row.when), frequency: text(row.frequency), duration: text(row.duration), notes: text(row.notes) }))
      : [blankMedicine()]);
    setTestsNeeded(text(query.data.testsNeeded));
    setAdvice(text(query.data.advice));
  }, [query.data]);

  const updateRow = <T,>(setRows: Dispatch<SetStateAction<T[]>>, index: number, patch: Partial<T>) =>
    setRows((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  const removeRow = <T,>(setRows: Dispatch<SetStateAction<T[]>>, index: number) =>
    setRows((current) => current.length > 1 ? current.filter((_, itemIndex) => itemIndex !== index) : current);
  const rowComplete = <T extends object>(row: T) => Object.values(row).every((value) => typeof value === 'string' && value.trim().length > 0);

  const submit = () => {
    const payload: UpsertDialysisPrescriptionPayload = {
      prescriptionDate,
      vitals: vitals.map((row) => ({ ...row })),
      complaints: complaints.map((row) => ({ ...row })),
      diagnoses: diagnoses.map((row) => ({ ...row })),
      medicines: medicines.map((row) => ({ ...row })),
      testsNeeded: testsNeeded || null,
      advice: advice || null,
    };
    const run = () => saveMutation.mutate(payload, {
      onSuccess: () => showToast(query.data ? 'Prescription updated successfully.' : 'Prescription created successfully.', 'success'),
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The prescription could not be saved.', 'error'),
    });
    if (!query.data) {
      run();
      return;
    }
    void confirmUpdate('this dialysis prescription').then((confirmed) => {
      if (confirmed) run();
    });
  };

  return (
    <div className="opd-prescription">
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>{query.data ? 'Update Prescription' : 'Add Prescription'} — {admission.patientSnapshot.name}</span></div>
        <p>Dialysis No {admission.dialysisNumber}{query.data ? ' · Saved' : ' · Draft'}</p>
      </header>

      <div className="opd-prescription-top">
        <span className="opd-prescription-tag"><IconBillCheck />Dialysis</span>
        <label><span className="opd-label-text">Date<span className="opd-required">*</span></span><input type="datetime-local" value={prescriptionDate} onChange={(event) => setPrescriptionDate(event.target.value)} /></label>
        <label><span className="opd-label-text">Patient Name<span className="opd-required">*</span></span><input value={`${admission.patientSnapshot.name}${admission.patientSnapshot.uhid ? ` (${admission.patientSnapshot.uhid})` : ''}`} readOnly /></label>
        <label><span className="opd-label-text">Doctor&apos;s Name<span className="opd-required">*</span></span><input value={`${doctorNameOrDash(admission.underDoctor?.name)}${admission.underDoctor?.department ? ` (${admission.underDoctor.department})` : ''}`} readOnly /></label>
      </div>

      <section className="opd-section"><h2>Vitals</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Height(cm)<span className="opd-required">*</span></th><th>Weight(kg)<span className="opd-required">*</span></th><th>Pulse(bpm)<span className="opd-required">*</span></th><th>BP(mmHg)<span className="opd-required">*</span></th><th>Temperature(F)<span className="opd-required">*</span></th><th className="opd-charge-narrow" /></tr></thead>
            <tbody>
              {vitals.map((row, index) => (
                <tr key={index}>
                  <td><input value={row.height} onChange={(event) => updateRow(setVitals, index, { height: event.target.value })} /></td>
                  <td><input value={row.weight} onChange={(event) => updateRow(setVitals, index, { weight: event.target.value })} /></td>
                  <td><input value={row.pulse} onChange={(event) => updateRow(setVitals, index, { pulse: event.target.value })} /></td>
                  <td className="opd-prescription-bp"><input value={row.bpSystolic} onChange={(event) => updateRow(setVitals, index, { bpSystolic: event.target.value })} /><span>/</span><input value={row.bpDiastolic} onChange={(event) => updateRow(setVitals, index, { bpDiastolic: event.target.value })} /></td>
                  <td><input value={row.temperature} onChange={(event) => updateRow(setVitals, index, { temperature: event.target.value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === vitals.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setVitals((current) => [...current, blankVitals()])} disabled={!rowComplete(row)} title={rowComplete(row) ? undefined : 'Fill in all required fields before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setVitals, index)} aria-label="Remove vitals row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Complaints</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Date<span className="opd-required">*</span></th><th>Complaints<span className="opd-required">*</span></th><th>Frequency<span className="opd-required">*</span></th><th>Severity<span className="opd-required">*</span></th><th>Duration<span className="opd-required">*</span></th><th className="opd-charge-narrow" /></tr></thead>
            <tbody>
              {complaints.map((row, index) => (
                <tr key={index}>
                  <td><input type="date" value={row.date} onChange={(event) => updateRow(setComplaints, index, { date: event.target.value })} /></td>
                  <td><input value={row.complaint} onChange={(event) => updateRow(setComplaints, index, { complaint: event.target.value })} /></td>
                  <td><Select value={row.frequency} options={FREQUENCY_OPTIONS} onChange={(value) => updateRow(setComplaints, index, { frequency: value })} /></td>
                  <td><Select value={row.severity} options={SEVERITY_OPTIONS} onChange={(value) => updateRow(setComplaints, index, { severity: value })} /></td>
                  <td><Select value={row.duration} options={DURATION_OPTIONS} onChange={(value) => updateRow(setComplaints, index, { duration: value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === complaints.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setComplaints((current) => [...current, blankComplaint()])} disabled={!rowComplete(row)} title={rowComplete(row) ? undefined : 'Fill in all required fields before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setComplaints, index)} aria-label="Remove complaint row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Diagnosis</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Date<span className="opd-required">*</span></th><th>Diagnosis<span className="opd-required">*</span></th><th>Duration<span className="opd-required">*</span></th><th className="opd-charge-narrow" /></tr></thead>
            <tbody>
              {diagnoses.map((row, index) => (
                <tr key={index}>
                  <td><input type="date" value={row.date} onChange={(event) => updateRow(setDiagnoses, index, { date: event.target.value })} /></td>
                  <td><Select value={row.diagnosis} options={DIAGNOSIS_OPTIONS} onChange={(value) => updateRow(setDiagnoses, index, { diagnosis: value })} /></td>
                  <td><Select value={row.duration} options={DURATION_OPTIONS} onChange={(value) => updateRow(setDiagnoses, index, { duration: value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === diagnoses.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setDiagnoses((current) => [...current, blankDiagnosis()])} disabled={!rowComplete(row)} title={rowComplete(row) ? undefined : 'Fill in all required fields before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setDiagnoses, index)} aria-label="Remove diagnosis row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Medicines</h2>
        <div className="table-scroll">
          <table className="opd-charge-table opd-prescription-table">
            <thead><tr><th>Composition<span className="opd-required">*</span></th><th>Medicine<span className="opd-required">*</span></th><th>Dose<span className="opd-required">*</span></th><th>When<span className="opd-required">*</span></th><th>Frequency<span className="opd-required">*</span></th><th>Duration<span className="opd-required">*</span></th><th>Notes/Instructions<span className="opd-required">*</span></th><th className="opd-charge-narrow" /></tr></thead>
            <tbody>
              {medicines.map((row, index) => (
                <tr key={index}>
                  <td><Select value={row.composition} options={COMPOSITION_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { composition: value })} /></td>
                  <td><Select value={row.medicine} options={MEDICINE_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { medicine: value })} /></td>
                  <td><Select value={row.dose} options={DOSE_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { dose: value })} /></td>
                  <td><Select value={row.when} options={WHEN_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { when: value })} /></td>
                  <td><Select value={row.frequency} options={FREQUENCY_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { frequency: value })} /></td>
                  <td><Select value={row.duration} options={DURATION_OPTIONS} onChange={(value) => updateRow(setMedicines, index, { duration: value })} /></td>
                  <td><input value={row.notes} onChange={(event) => updateRow(setMedicines, index, { notes: event.target.value })} /></td>
                  <td className="opd-charge-narrow">
                    {index === medicines.length - 1
                      ? <button className="opd-add" type="button" onClick={() => setMedicines((current) => [...current, blankMedicine()])} disabled={!rowComplete(row)} title={rowComplete(row) ? undefined : 'Fill in all required fields before adding another row'}>+</button>
                      : <button type="button" onClick={() => removeRow(setMedicines, index)} aria-label="Remove medicine row">×</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section"><h2>Tests Needed</h2>
        <input value={testsNeeded} onChange={(event) => setTestsNeeded(event.target.value)} placeholder="Enter tests needed" />
      </section>

      <section className="opd-section">
        <CkEditorField label="Advice" value={advice} onChange={setAdvice} placeholder="Advice for the patient" />
      </section>

      <div className="opd-form-actions">
        <button className="button button-primary" type="button" disabled={saveMutation.isPending} onClick={submit}>{saveMutation.isPending ? 'Saving…' : query.data ? 'Update Prescription' : 'Create E-Prescription'}</button>
      </div>
    </div>
  );
};
